import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Radio, Checkbox, Input, Typography, Upload, message, Space, Row, Col, Modal, Spin } from "antd";
import { PlusOutlined, CloseOutlined, RightOutlined, DownOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import copyIcon from "../../assets/svg/copy.svg";
import {
  getRiskEvaluation,
  saveCustomerRiskAssessment,
  getOrderDetail,
  ORDER_STATUS_DICT,
  OrderStatus,
  getProductCompanyList,
  uploadFile,
  uploadAttachment,
  FileType,
  getSignatureConfig,
  getOrderAttachments,
  getCraResultByAnswer,
  CraProject,
  CraRiskResult,
  deleteAttachment,
  getCraAttachmentConfig,
  saveCraInsuredPolicy,
  RiskAssessmentAuditStatus,
  getUserInfo,
} from "@ifa/utils";
import type { UserInfo } from "@ifa/utils";
import type { OrderCra, OrderCraDTO, FormData as ApiFormData, OrderDetail, HighPremiumOrder, ProductCompanyListVO, OrderAttachmentDTO, CraResultVO, OrderAttachmentConfig } from "@ifa/utils";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import styles from "../OrderForm.module.css";
import type { RiskAssessmentVO } from "./types/risk";
import pdfIcon from "../../assets/img/pdf.png";
import excelIcon from "../../assets/img/excel.png";
import wordIcon from "../../assets/img/word.png";
import type { UploadFile } from "antd/es/upload/interface";
import { MobileDatePicker } from "../../components/MobilePicker/MobileDatePicker";
import { FilePreviewModal } from "../../components/FilePreviewModal";
import { HighPremiumOrderModal } from "./components/HighPremiumOrderModal";
import { HighPremiumOrderCard } from "./components/HighPremiumOrderCard";
import { SelectInput } from "../../components/form/SelectInput";
import { useImageCompression } from "@ifa/ui";
import { useIOSConfirm } from "../../hooks/useIOSConfirm";
import { SignaturePad } from "./SignaturePad";
import type { SignaturePadRef } from "./SignaturePad";

export const DatePickerInput = ({ value, onChange, title, placeholder = "請選擇" }: any) => {
  const [visible, setVisible] = useState(false);

  // 格式化显示值
  const displayValue = value ? (dayjs.isDayjs(value) ? value.format("YYYY-MM-DD") : value) : "";

  return (
    <>
      <div
        onClick={() => setVisible(true)}
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: "8px",
          padding: "4px 11px",
          height: 40,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          background: "#fff",
          width: "100%",
        }}
      >
        <span style={{ color: displayValue ? "#333" : "#bfbfbf", fontSize: 14 }}>{displayValue || placeholder}</span>
        <DownOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
      </div>
      <MobileDatePicker
        visible={visible}
        onClose={() => setVisible(false)}
        title={title}
        value={value}
        onChange={(dateString) => {
          // 直接返回日期字符串，不转换为 Dayjs
          onChange?.(dateString || null);
        }}
      />
    </>
  );
};

const RISK_ASSESSMENT_QUESTION_NUMBER_MAP: Record<string, string> = {
  pep: "1-1",
  fullUboAndDirectorList: "1-2",
  customerMediumRiskSituation: "1-3",
  customerHighRiskSituation: "1-4",
  validIdentityDocuments: "2-1",
  cooperative: "2-2",
  sourceOfFundsConsistent: "3-1",
  notHighRiskBusiness: "3-2",
  paidInitialPremium: "4-1",
  fromOwnAccount: "4-2",
  thirdPartyPayerRelation: "4-2",
  thirdPartyPayerInfo: "4-2",
  initialPremiumPaymentMethod: "4-3",
  referredByThirdParty: "5-1",
  instructionThroughThirdParty: "5-2",
  thirdPartyInstructionProof: "5-2",
  faceToFaceCommunication: "6-1",
  needsNonFaceToFaceSafeguards: "6-2",
  residentInHK: "7-1",
  nonHKResidenceCategory: "7-2",
  providesAnonymity: "8-1",
  involvesLargeCash: "8-2",
  frequentPurchasesWithoutReason: "8-3",
  supportsHighFrequencyIrregularActivity: "8-4",
  allowsLargeWithdrawals: "8-5",
  exceedsHighPremiumThreshold: "8-6",
};

const RISK_ASSESSMENT_INPUT_FIELDS = new Set(["thirdPartyPayerInfo", "thirdPartyInstructionProof"]);
const RISK_ASSESSMENT_SELECT_FIELDS = new Set(["thirdPartyPayerRelation"]);
const RISK_ASSESSMENT_CHECKBOX_FIELDS = new Set(["initialPremiumPaymentMethod"]);
const RISK_ASSESSMENT_CHECKBOX_OPTION_KEYS = ["A", "B", "C", "D", "E", "F", "G"];

const RISK_ASSESSMENT_RADIO_OPTION_MAP: Record<string, string[]> = {
  pep: ["A", "B"],
  customerMediumRiskSituation: ["A", "B"],
  customerHighRiskSituation: ["A", "B"],
  validIdentityDocuments: ["A", "B"],
  cooperative: ["A", "B"],
  sourceOfFundsConsistent: ["A", "B"],
  notHighRiskBusiness: ["A", "B"],
  paidInitialPremium: ["A", "B"],
  fromOwnAccount: ["A", "B"],
  involvesLargeCash: ["A", "B"],
  frequentPurchasesWithoutReason: ["A", "B"],
  supportsHighFrequencyIrregularActivity: ["A", "B"],
  allowsLargeWithdrawals: ["A", "B"],
  exceedsHighPremiumThreshold: ["A", "B"],
  providesAnonymity: ["A", "B"],
  faceToFaceCommunication: ["A", "B"],
  residentInHK: ["A", "B"],
  fullUboAndDirectorList: ["A", "B", "C"],
  referredByThirdParty: ["A", "B", "C"],
  instructionThroughThirdParty: ["A", "B", "C"],
  needsNonFaceToFaceSafeguards: ["A", "B", "C"],
  nonHKResidenceCategory: ["A", "B", "C"],
};

const RISK_ASSESSMENT_FIELD_KEYS = Array.from(
  new Set([
    ...Object.keys(RISK_ASSESSMENT_QUESTION_NUMBER_MAP),
    ...Array.from(RISK_ASSESSMENT_INPUT_FIELDS),
    ...Array.from(RISK_ASSESSMENT_SELECT_FIELDS),
    ...Array.from(RISK_ASSESSMENT_CHECKBOX_FIELDS),
    ...Object.keys(RISK_ASSESSMENT_RADIO_OPTION_MAP),
  ]),
);

const buildRiskAssessmentAnswers = (values: Record<string, any>, originalAnswer?: ApiFormData[]) => {
  const nextAnswer = cloneDeep(originalAnswer || []);
  const fieldKeys = Array.from(new Set([...RISK_ASSESSMENT_FIELD_KEYS, ...Object.keys(values)]));

  fieldKeys.forEach((key) => {
    const rawValue = values[key];

    let item = nextAnswer.find((question) => question.question === key);
    if (!item) {
      item = { question: key, items: [] };
      nextAnswer.push(item);
    }

    if (RISK_ASSESSMENT_QUESTION_NUMBER_MAP[key]) {
      item.questionNumber = RISK_ASSESSMENT_QUESTION_NUMBER_MAP[key];
    }

    if (RISK_ASSESSMENT_INPUT_FIELDS.has(key) || RISK_ASSESSMENT_SELECT_FIELDS.has(key)) {
      const normalizedValue = rawValue ?? "";
      item.type = "INPUT";
      item.value = normalizedValue;
      item.items = [
        {
          key: "A",
          value: normalizedValue,
        },
      ];
      return;
    }

    if (RISK_ASSESSMENT_CHECKBOX_FIELDS.has(key)) {
      const normalizedValue = Array.isArray(rawValue) ? rawValue : [];
      item.type = "CHECKBOX";
      item.value = normalizedValue;
      item.items = RISK_ASSESSMENT_CHECKBOX_OPTION_KEYS.map((optionKey) => ({
        key: optionKey,
        value: normalizedValue.includes(optionKey),
      }));
      return;
    }

    const radioOptions = RISK_ASSESSMENT_RADIO_OPTION_MAP[key];
    if (radioOptions) {
      const normalizedValue = rawValue ?? null;
      item.type = "RADIO";
      item.value = normalizedValue;
      item.items = radioOptions.map((optionKey) => ({
        key: optionKey,
        value: normalizedValue === optionKey,
      }));
      return;
    }

    if (rawValue !== undefined) {
      item.value = rawValue;
    }
  });

  return nextAnswer;
};

export const RiskAssessmentForm: React.FC<{ hideHeader?: boolean; readOnly?: boolean }> = ({ hideHeader = false, readOnly = false }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const fromOwnAccount = Form.useWatch("fromOwnAccount", form);
  const referredByThirdParty = Form.useWatch("referredByThirdParty", form);
  const instructionThroughThirdParty = Form.useWatch("instructionThroughThirdParty", form);
  const { confirm } = useIOSConfirm();

  // 使用图片压缩 Hook
  const { handleBeforeUpload } = useImageCompression({
    confirm,
  });

  const [loading, setLoading] = useState(false);
  const [originalCraData, setOriginalCraData] = useState<OrderCra | null>(null);
  const [orderData, setOrderData] = useState<OrderDetail | null>(null);

  // ORDER_AUDIT / COMPLIANCE_AUDIT / COMPLETED 状态下强制只读
  // 只在待审核（PENDING_REVIEW）和审核通过（APPROVED）时只读
  const isReadOnly =
    readOnly ||
    orderData?.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Submitted ||
    orderData?.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Approved ||
    orderData?.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Initial;
  const [highPremiumOrders, setHighPremiumOrders] = useState<(HighPremiumOrder & { readOnly?: boolean })[]>([]);
  const [companyList, setCompanyList] = useState<ProductCompanyListVO[]>([]);
  const [craAttachmentConfig, setCraAttachmentConfig] = useState<OrderAttachmentConfig[]>([]);
  const [signatureImage, setSignatureImage] = useState<string>(""); // 业务代表签名

  // High Premium Order Modal State
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<(HighPremiumOrder & { key?: React.Key }) | undefined>(undefined);

  // Risk Result Modal State
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [craResults, setCraResults] = useState<CraResultVO[]>([]);
  const [loadingRiskResults, setLoadingRiskResults] = useState(false);

  // Signature Modal State
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [tempSignature, setTempSignature] = useState<string>(""); // 临时签名，用于弹窗中
  const [isSignatureReady, setIsSignatureReady] = useState(false);
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);

  // 获取当前登录用户信息（业务代表）
  useEffect(() => {
    const uid = localStorage.getItem("uid");
    if (uid) {
      getUserInfo({ uid: Number(uid) }).then((res) => {
        setCurrentUser(res);
      });
    }
  }, []);

  // Fetch data from API
  useEffect(() => {
    // Fetch Company List
    getProductCompanyList().then((res) => {
      setCompanyList(res || []);
    });

    if (orderId) {
      setLoading(true);

      // Fetch Order Detail for header info
      getOrderDetail(Number(orderId)).then((res) => {
        if (res && res) {
          setOrderData(res);
        }
      });

      getRiskEvaluation(Number(orderId))
        .then((res) => {
          console.log(res);

          if (res) {
            const craData = res;
            setOriginalCraData(craData);
            setHighPremiumOrders(craData.insuredPolicy.highPremiumOrders || []);
            const initialValues: any = {};

            // Helper to map API response to form values
            const directFields = [
              "pep",
              "fullUboAndDirectorList",
              "customerMediumRiskSituation",
              "customerHighRiskSituation",
              "validIdentityDocuments",
              "cooperative",
              "sourceOfFundsConsistent",
              "notHighRiskBusiness",
              "paidInitialPremium",
              "fromOwnAccount",
              "thirdPartyPayerRelation",
              "thirdPartyPayerInfo",
              "initialPremiumPaymentMethod",
              "referredByThirdParty",
              "instructionThroughThirdParty",
              "thirdPartyInstructionProof",
              "faceToFaceCommunication",
              "needsNonFaceToFaceSafeguards",
              "residentInHK",
              "nonHKResidenceCategory",
              "providesAnonymity",
              "involvesLargeCash",
              "frequentPurchasesWithoutReason",
              "supportsHighFrequencyIrregularActivity",
              "allowsLargeWithdrawals",
              "exceedsHighPremiumThreshold",
            ];

            // 1. Map from answer array (Primary source)
            if (craData.answer && Array.isArray(craData.answer)) {
              craData.answer.forEach((item: any) => {
                const key = item.question;
                if (key && directFields.includes(key)) {
                  // Check item type to determine how to process
                  if (item.type === "INPUT") {
                    // For INPUT type, use value directly or from first item
                    if (item.value !== undefined) {
                      initialValues[key] = item.value;
                    } else if (item.items && item.items.length > 0) {
                      initialValues[key] = item.items[0].value;
                    }
                  } else if (item.type === "CHECKBOX") {
                    // For CHECKBOX type, collect all keys where value is true
                    if (item.items && item.items.length > 0) {
                      const selectedItems = item.items.filter((opt: any) => opt.value === true);
                      initialValues[key] = selectedItems.map((opt: any) => opt.key);
                    }
                  } else if (item.type === "RADIO") {
                    // For RADIO type, find the key where value is true
                    if (item.items && item.items.length > 0) {
                      const selectedItem = item.items.find((opt: any) => opt.value === true);
                      if (selectedItem) {
                        initialValues[key] = selectedItem.key;
                      }
                    }
                  } else {
                    // Fallback for items without type (backward compatibility)
                    if (item.items && item.items.length > 0) {
                      const selectedItems = item.items.filter((opt: any) => opt.value === true);
                      if (selectedItems.length > 0) {
                        if (key === "initialPremiumPaymentMethod") {
                          // For multiple selection, map to array of keys
                          initialValues[key] = selectedItems.map((opt: any) => opt.key);
                        } else {
                          // For single selection, map to the first key
                          initialValues[key] = selectedItems[0].key;
                        }
                      }
                    } else if (item.value !== undefined) {
                      // For input fields where items is empty, use the value directly
                      initialValues[key] = item.value;
                    }
                  }
                }
              });
            }

            // 2. Map from direct properties (Fallback)
            directFields.forEach((field) => {
              if (initialValues[field] === undefined && craData[field] !== undefined && craData[field] !== null) {
                initialValues[field] = craData[field];
              }
            });

            // 3. Set default value for fullUboAndDirectorList to "C"
            if (initialValues.fullUboAndDirectorList === undefined) {
              initialValues.fullUboAndDirectorList = "C";
            }

            // 4. 恢复业务代表签名
            if (craData.signatureImage) {
              setSignatureImage(craData.signatureImage);
            }

            console.log("Mapped Risk Assessment Data:", initialValues);
            form.setFieldsValue(initialValues);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch risk evaluation:", err);
          message.error("獲取風險評估數據失敗");
        })
        .finally(() => {
          setLoading(false);
        });

      // Fetch existing CRA attachments
      const fetchCraAttachments = async () => {
        const craTypes = [
          FileType.CraBrokerReport,
          FileType.CraFinancialQuestionnaire,
          FileType.RiskEvaluationFormAttachment,
          FileType.CraAssetIncomeProof,
          FileType.CraPremiumPaymentProof,
          FileType.CraThiredPartyRelaTionShipCertificate,
        ];

        try {
          const results = await Promise.all(craTypes.map((type) => getOrderAttachments(Number(orderId), type as any).catch(() => [])));

          const newFiles: Record<string, UploadFile[]> = {};

          results.forEach((files, index) => {
            const type = craTypes[index];
            if (files && files.length > 0) {
              newFiles[type] = files.map((f: any) => ({
                uid: f.id ? String(f.id) : f.attachmentId ? String(f.attachmentId) : String(Math.random()),
                name: f.name || f.fileName || "Unknown File",
                status: "done",
                url: f.url || f.path || f.fileUrl,
                path: f.path,
                ossPath: f.ossPath,
                attachmentId: f.attachmentId,
              }));
            }
          });

          if (Object.keys(newFiles).length > 0) {
            setUploadFiles((prev) => ({ ...prev, ...newFiles }));
          }
        } catch (error) {
          console.error("Failed to fetch CRA attachments:", error);
        }
      };
      fetchCraAttachments();

      // Fetch Signature Config for uploaded files
      getSignatureConfig(Number(orderId)).then((res) => {
        if (res && res.configs) {
          const loadedFiles: Record<string, { name: string; url: string }[]> = {};
          res.configs.forEach((item) => {
            // Only add if type and fileUrl are present
            if (item.type && item._fileUrl) {
              if (!loadedFiles[item.type]) {
                loadedFiles[item.type] = [];
              }
              loadedFiles[item.type].push({
                name: item.name,
                url: item._fileUrl,
              });
            }
          });
          setUploadFiles((prev) => ({ ...prev, ...loadedFiles }));
        }
      });

      // Fetch CRA attachment config
      getCraAttachmentConfig(Number(orderId))
        .then((res) => {
          if (res && Array.isArray(res)) {
            setCraAttachmentConfig(res);

            // 如果接口返回有值，自动勾选 exceedsHighPremiumThreshold 为 "A"
            if (res.length > 0) {
              form.setFieldsValue({
                exceedsHighPremiumThreshold: "A",
              });
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch CRA attachment config:", err);
        });
    }
  }, [orderId, form]);

  useEffect(() => {
    if (fromOwnAccount === "B") return;

    if (form.getFieldValue("thirdPartyPayerRelation") || form.getFieldValue("thirdPartyPayerInfo")) {
      form.setFieldsValue({
        thirdPartyPayerRelation: undefined,
        thirdPartyPayerInfo: undefined,
      });
    }
  }, [fromOwnAccount, form]);

  // CRA - 分銷/交付風險（分銷管道）
  // 規則：
  // - 客戶是否由第三方介紹 = A（是） => 客戶的投保指示是否通過第三方傳達？固定為 B（否），且不可修改
  // - 客戶是否由第三方介紹 = B（否）或 C（N/A） => 固定為 C（N/A），且不可修改
  useEffect(() => {
    if (!referredByThirdParty) return;

    if (referredByThirdParty === "A") {
      if (instructionThroughThirdParty !== "B" || form.getFieldValue("thirdPartyInstructionProof")) {
        form.setFieldsValue({
          instructionThroughThirdParty: "B",
          thirdPartyInstructionProof: undefined,
        });
      }
      return;
    }

    if (referredByThirdParty === "B" || referredByThirdParty === "C") {
      if (instructionThroughThirdParty !== "C" || form.getFieldValue("thirdPartyInstructionProof")) {
        form.setFieldsValue({
          instructionThroughThirdParty: "C",
          thirdPartyInstructionProof: undefined,
        });
      }
    }
  }, [referredByThirdParty, instructionThroughThirdParty, form]);

  const onFinish = async (values: RiskAssessmentVO) => {
    if (!orderId) return;

    // Ensure fullUboAndDirectorList defaults to "C" if not set
    if (!values.fullUboAndDirectorList) {
      values.fullUboAndDirectorList = "C";
    }

    setLoading(true);
    try {
      const newAnswer = buildRiskAssessmentAnswers(values, originalCraData?.answer);

      console.log(newAnswer);

      const payload: OrderCraDTO = {
        orderId: Number(orderId),
        answer: newAnswer,
        highPremiumOrders: highPremiumOrders.filter((item) => !item.isInternal),
        signatureImage: signatureImage, // 包含业务代表签名
      };

      await saveCustomerRiskAssessment(payload);
      message.success("儲存成功");

      // 提交审核成功后返回列表页
      setTimeout(() => {
        // Check URL params for source information
        const searchParams = new URLSearchParams(location.search);
        const from = searchParams.get("from");
        const customerId = searchParams.get("customerId");

        if (from === "customer-detail" && customerId) {
          // Return to customer detail page
          navigate(`/customers/${customerId}`);
        } else {
          // Return to order list
          navigate("/orders");
        }
      }, 500); // 延迟500ms，让用户看到成功提示
    } catch (err) {
      console.error("Failed to save risk evaluation:", err);
      message.error("儲存失敗");
    } finally {
      setLoading(false);
    }
  };

  // 处理提交审核按钮点击 - 打开签名弹窗
  const handleSubmitClick = async () => {
    try {
      // 验证表单
      await form.validateFields();

      // 表单验证通过，打开签名弹窗
      setTempSignature(signatureImage);
      setIsSignatureReady(!!signatureImage);
      setIsSignatureModalOpen(true);
    } catch (error) {
      console.log("表单验证失败:", error);
      onFinishFailed(error);
    }
  };

  // 处理确认签名按钮点击 - 检查签名是否存在
  const handleConfirmSignature = () => {
    // 检查是否有签名内容
    if (!signaturePadRef.current?.hasContent()) {
      message.error("請先簽名後再提交審核");
      return;
    }

    // 调用 SignaturePad 的 save 方法，会触发 handleSaveSignature
    signaturePadRef.current?.save();
  };

  // 保存签名（SignaturePad 的 onSave 回调）
  const handleSaveSignature = async (dataUrl: string) => {
    // 保存签名到临时状态
    setTempSignature(dataUrl);
    // 保存签名到主状态
    setSignatureImage(dataUrl);
    // 关闭弹窗
    setIsSignatureModalOpen(false);
    setIsSignatureReady(false);

    // 直接调用提交逻辑
    if (!orderId) return;

    setLoading(true);
    try {
      // 获取表单值
      const values = form.getFieldsValue();

      const newAnswer = buildRiskAssessmentAnswers(values, originalCraData?.answer);

      const payload: OrderCraDTO = {
        orderId: Number(orderId),
        answer: newAnswer,
        highPremiumOrders: highPremiumOrders.filter((item) => !item.isInternal),
        signatureImage: dataUrl, // 使用新签名
      };

      await saveCustomerRiskAssessment(payload);
      message.success("儲存成功");

      // 提交审核成功后返回列表页
      setTimeout(() => {
        const searchParams = new URLSearchParams(location.search);
        const from = searchParams.get("from");
        const customerId = searchParams.get("customerId");

        if (from === "customer-detail" && customerId) {
          navigate(`/customers/${customerId}`);
        } else {
          navigate("/orders");
        }
      }, 500);
    } catch (err) {
      console.error("Failed to save risk evaluation:", err);
      message.error("儲存失敗");
    } finally {
      setLoading(false);
    }
  };

  // 处理表单验证失败
  const onFinishFailed = (errorInfo: any) => {
    console.log("表单验证失败:", errorInfo);

    // 获取所有错误字段
    const errorFields = errorInfo.errorFields || [];

    if (errorFields.length === 0) return;

    // 只获取第一个错误字段
    const firstErrorField = errorFields[0];
    const fieldName = firstErrorField.name[0];

    // 获取字段的 label（通过查找表单项）
    let fieldLabel = fieldName;

    // 尝试从表单中获取字段 label
    const formItem = document.querySelector(`[id="${fieldName}"]`)?.closest(".ant-form-item");
    if (formItem) {
      const labelElement = formItem.querySelector(".ant-form-item-label label");
      if (labelElement) {
        fieldLabel = labelElement.textContent || fieldName;
      }
    }

    // 使用 message.error 显示错误提示
    message.error(`請填寫必填項：${fieldLabel}`);

    // 滚动到第一个错误字段
    form.scrollToField(fieldName, {
      behavior: "smooth",
      block: "center",
      scrollMode: "if-needed",
    });
  };

  const add = () => {
    setEditingOrder(undefined);
    setIsOrderModalOpen(true);
  };

  const edit = (record: HighPremiumOrder & { key: React.Key }) => {
    console.log("点击编辑的保单数据:", record);
    console.log("是否为内部保单:", record.isInternal);
    setEditingOrder(record);
    setIsOrderModalOpen(true);
  };

  const handleSaveOrder = async (values: HighPremiumOrder) => {
    if (!orderId) {
      message.error("訂單ID不存在");
      return;
    }
    console.log("表单提交的值:", values);
    console.log("当前编辑的保单:", editingOrder);
    try {
      const newData = [...highPremiumOrders];

      if (editingOrder) {
        // 編輯現有訂單 - 使用更精确的匹配条件,包括 isInternal 字段
        const index = newData.findIndex((item: any) => {
          // 优先使用 key 或 id 匹配
          const keyMatch = (item.key && item.key === editingOrder.key) || (item.id && item.id === editingOrder.id);
          // 同时检查 orderNo 和 isInternal,确保匹配到正确的保单
          const orderMatch = item.orderNo === editingOrder.orderNo && item.isInternal === editingOrder.isInternal;
          return keyMatch || orderMatch;
        });

        console.log("找到的匹配索引:", index);

        if (index > -1) {
          console.log("原数据:", newData[index]);
          newData[index] = {
            ...newData[index],
            ...values,
          };
          console.log("更新后数据:", newData[index]);
        } else {
          console.error("未找到匹配的保单!");
        }
      } else {
        // 新增訂單
        newData.push({
          ...values,
        } as any);
      }
      console.log("保存前的完整数据 newData:", newData);

      const dataToSave = newData.filter((item) => !item.isInternal);
      console.log("过滤后要发送给后端的数据:", dataToSave);

      // 調用 API 保存
      await saveCraInsuredPolicy({
        orderId: Number(orderId),
        highPremiumOrders: dataToSave,
      });

      // 刷新風險評估數據以獲取最新的保單列表
      const craData = await getRiskEvaluation(Number(orderId));
      console.log("保存后从后端获取的数据:", craData?.insuredPolicy?.highPremiumOrders);

      if (craData && craData.insuredPolicy) {
        setOriginalCraData(craData);
        setHighPremiumOrders(craData.insuredPolicy.highPremiumOrders || []);
      }

      message.success("儲存成功");
      setIsOrderModalOpen(false);
      setEditingOrder(undefined);

      // 刷新附件配置
      const config = await getCraAttachmentConfig(Number(orderId));
      if (config && Array.isArray(config)) {
        setCraAttachmentConfig(config);
      }
    } catch (err) {
      console.error("保存失敗:", err);
      throw err;
    }
  };
  const handleBack = () => {
    // Check URL params for source information
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get("from");
    const customerId = searchParams.get("customerId");

    if (from === "customer-detail" && customerId) {
      // Return to customer detail page
      navigate(`/customers/${customerId}`);
    } else {
      // Return to order list
      navigate("/orders");
    }
  };

  const handleDelete = async (key: React.Key) => {
    if (!orderId) {
      message.error("訂單ID不存在");
      return;
    }

    try {
      const newData = highPremiumOrders.filter((item: any) => item.orderNo !== key);

      // 調用 API 保存更新後的列表
      await saveCraInsuredPolicy({
        orderId: Number(orderId),
        highPremiumOrders: newData.filter((item) => !item.isInternal),
      });

      // 刷新風險評估數據以獲取最新的保單列表
      const craData = await getRiskEvaluation(Number(orderId));
      if (craData && craData.insuredPolicy) {
        setOriginalCraData(craData);
        setHighPremiumOrders(craData.insuredPolicy.highPremiumOrders || []);
      }

      message.success("刪除成功");

      // 刷新附件配置
      const config = await getCraAttachmentConfig(Number(orderId));
      if (config && Array.isArray(config)) {
        setCraAttachmentConfig(config);
      }
    } catch (err) {
      console.error("刪除失敗:", err);
      // 錯誤時不需要恢復，因為本地狀態未變
    }
  };

  const [uploadFiles, setUploadFiles] = useState<Record<string, UploadFile[]>>({});

  // Preview Modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string }>();

  const handlePreview = async (file: UploadFile) => {
    // 优先使用 ossPath，如果没有则使用 path
    const filePath = (file as any).ossPath || (file as any).path || file.response || file.url;

    if (!filePath) {
      message.error("檔案路徑不可用");
      return;
    }

    // 对于本地新上传的文件（有 originFileObj），使用 FileReader 读取
    if (file.originFileObj && !file.url) {
      const previewUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as Blob);
        reader.onload = () => resolve(reader.result as string);
      });
      setPreviewFile({ url: previewUrl, name: file.name || "unnamed" });
      setPreviewOpen(true);
      return;
    }

    // 其他情况：传递 ossPath/path 给 FilePreviewModal，让它通过 API 获取
    setPreviewFile({ url: filePath, name: file.name || "unnamed" });
    setPreviewOpen(true);
  };

  // ... (previous useEffect code)

  const handleUpload = async (options: any, fileType: FileType) => {
    const { onSuccess, onError, file } = options;
    if (!orderId) {
      message.error("訂單ID不存在");
      onError(new Error("No Order ID"));
      return;
    }

    // 使用 Hook 处理图片压缩
    const fileToUpload = await handleBeforeUpload(file);
    if (fileToUpload === false) {
      // 用户取消上传
      onError(new Error("User cancelled"));
      return;
    }

    try {
      // 1. Upload File to Object Storage
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("module", "ORDER");
      // Use orderNo if available, otherwise use orderId as fallback
      formData.append("key", orderData?.orderNo || String(orderId));

      const filePath = await uploadFile(formData);

      // 2. Link Attachment to Order
      const uploadData: OrderAttachmentDTO = {
        orderId: Number(orderId),
        token: filePath,
        type: fileType,
      };

      await uploadAttachment(uploadData);

      message.success(`上載成功`);

      // Refresh attachments to get IDs for correct deletion
      const files = await getOrderAttachments(Number(orderId), fileType as any);
      const mappedFiles = files.map((f: any) => ({
        uid: f.id ? String(f.id) : f.attachmentId ? String(f.attachmentId) : String(Math.random()),
        name: f.name || f.fileName || "Unknown File",
        status: "done" as const,
        url: f.url || f.path || f.fileUrl,
        path: f.path,
        ossPath: f.ossPath,
        attachmentId: f.attachmentId,
      }));

      setUploadFiles((prev) => ({
        ...prev,
        [fileType]: mappedFiles,
      }));

      onSuccess(filePath, fileToUpload);
    } catch (err) {
      console.error(err);
      message.error(`${file.name} 上載失敗`);
      onError(err);
    }
  };

  const handleRemove = async (file: UploadFile, fileType: FileType) => {
    const attachmentId = (file as any).attachmentId;

    if (attachmentId && orderId) {
      try {
        await deleteAttachment(Number(orderId), Number(attachmentId));
        message.success("刪除成功");
      } catch (err) {
        console.error("Failed to delete attachment:", err);
        message.error("刪除失敗");
        return false;
      }
    }

    setUploadFiles((prev) => ({
      ...prev,
      [fileType]: (prev[fileType] || []).filter((f) => f.uid !== file.uid),
    }));
    return true;
  };

  const renderUploadBox = (fileType: FileType, label: string, isRequired: boolean = false) => {
    let displayFiles = uploadFiles[fileType] || [];
    // Append insuredPolicy files to AssetIncomeProof
    if (fileType === FileType.CraAssetIncomeProof) {
      const insuredPolicyFiles = uploadFiles["INSURED_POLICY"] || [];
      displayFiles = [...displayFiles, ...insuredPolicyFiles];
    }

    return (
      <div className="upload-item-container">
        <Upload
          listType="picture-card"
          fileList={displayFiles}
          customRequest={(options) => handleUpload(options, fileType)}
          onRemove={(file) => handleRemove(file, fileType)}
          onPreview={handlePreview}
          disabled={isReadOnly}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: !isReadOnly }}
          itemRender={(originNode, file, fileList, actions) => {
            const fileName = file.name?.toLowerCase() || "";
            let iconSrc = null;
            let isImage = false;

            if (file.status !== "uploading") {
              if (fileName.endsWith(".pdf")) {
                iconSrc = pdfIcon;
              } else if (/\.(xls|xlsx|csv)$/.test(fileName)) {
                iconSrc = excelIcon;
              } else if (/\.(doc|docx)$/.test(fileName)) {
                iconSrc = wordIcon;
              } else {
                // Assume image if not document types above
                isImage = true;
              }
            }

            return (
              <div
                className="ant-upload-list-picture-card-container"
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginRight: 8,
                  marginBottom: 8,
                  width: 160,
                  height: 110,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: 8,
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    overflow: "hidden",
                    position: "relative",
                  }}
                  onClick={() => handlePreview(file)}
                >
                  {iconSrc ? (
                    <img src={iconSrc} alt={file.name} style={{ width: 60, height: 60, objectFit: "contain" }} />
                  ) : isImage ? (
                    <img
                      src={file.thumbUrl || file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "")}
                      alt={file.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    // Fallback or uploading state
                    originNode
                  )}
                </div>
                {!isReadOnly && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      actions.remove();
                    }}
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      background: "#ff4d4f",
                      color: "white",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 10,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    <CloseOutlined style={{ fontSize: 14 }} />
                  </div>
                )}
              </div>
            );
          }}
        >
          {displayFiles.length < 1 && !isReadOnly && (
            <div>
              <PlusOutlined style={{ fontSize: 24, color: "#999" }} />
              <div style={{ marginTop: 8, color: "#999" }}>上載</div>
            </div>
          )}
        </Upload>
        <div className="upload-label" style={{ marginTop: 8, color: "#666", fontSize: 14 }}>
          {isRequired && <span className="required-mark">*</span>}
          {label}
        </div>
      </div>
    );
  };

  // Assign keys to highPremiumOrders if missing
  const productData = highPremiumOrders.map((item: any, index: number) => ({
    ...item,
    key: item.key || item.id || `temp-${index}`,
  }));

  const annualTotalPremium = useMemo(
    () =>
      highPremiumOrders.reduce(
        (total, item) => total + Number(item?.annualPremium || 0),
        0,
      ),
    [highPremiumOrders],
  );

  const totalOneTimePayment = useMemo(
    () =>
      highPremiumOrders.reduce(
        (total, item) => total + Number(item?.oneTimePremium || 0),
        0,
      ),
    [highPremiumOrders],
  );

  // CRA 項目類型中文映射
  const CRA_PROJECT_LABELS: Record<CraProject, string> = {
    [CraProject.CustomerRisk]: "客戶風險",
    [CraProject.DistributionOrDeliveryRisk]: "分期/支付風險",
    [CraProject.CountryOrRegionalRisk]: "國家或地區風險",
    [CraProject.ProductServiceRisk]: "產品/服務風險",
    [CraProject.FinalAmlCftRiskAssessment]: "最終AML/CTF風險評估",
  };

  // CRA 風險結果映射（後端枚舉 -> 前端顯示格式）
  const mapCraRiskResult = (result?: CraRiskResult): "LOW" | "MEDIUM" | "HIGH" | "UNKNOWN" => {
    switch (result) {
      case CraRiskResult.NoRisk:
      case CraRiskResult.LowRisk:
        return "LOW";
      case CraRiskResult.MediumRisk:
        return "MEDIUM";
      case CraRiskResult.HighRisk:
        return "HIGH";
      default:
        return "UNKNOWN";
    }
  };

  const handleViewRiskResults = async () => {
    if (!orderId) {
      message.error("訂單ID不存在");
      return;
    }

    // 先进行表单验证
    try {
      await form.validateFields();
    } catch (errorInfo: any) {
      console.log("表单验证失败:", errorInfo);

      // 获取所有错误字段
      const errorFields = errorInfo.errorFields || [];

      if (errorFields.length > 0) {
        // 只获取第一个错误字段
        const firstErrorField = errorFields[0];
        const fieldName = firstErrorField.name[0];

        // 获取字段的 label（通过查找表单项）
        let fieldLabel = fieldName;

        // 尝试从表单中获取字段 label
        const formItem = document.querySelector(`[id="${fieldName}"]`)?.closest(".ant-form-item");
        if (formItem) {
          const labelElement = formItem.querySelector(".ant-form-item-label label");
          if (labelElement) {
            fieldLabel = labelElement.textContent || fieldName;
          }
        }

        // 使用 message.error 显示错误提示
        message.error(`請填寫必填項：${fieldLabel}`);

        // 滚动到第一个错误字段
        form.scrollToField(fieldName, {
          behavior: "smooth",
          block: "center",
          scrollMode: "if-needed",
        });
      }
      return;
    }

    setLoadingRiskResults(true);
    setIsRiskModalOpen(true);

    try {
      // 获取当前表单值
      const values = form.getFieldsValue();

      // 确保 fullUboAndDirectorList 有默认值
      if (!values.fullUboAndDirectorList) {
        values.fullUboAndDirectorList = "C";
      }

      const answer = buildRiskAssessmentAnswers(values, originalCraData?.answer);

      // 调用新的 POST 接口
      const craData = await getCraResultByAnswer(answer);
      if (craData && craData.length > 0) {
        setCraResults(craData);
      } else {
        setCraResults([]);
      }
    } catch (error) {
      console.error("Failed to fetch risk results:", error);
      message.error("獲取風險評估結果失敗");
    } finally {
      setLoadingRiskResults(false);
    }
  };

  return (
    <div className={`${styles["order-form-container"]}`}>
      {/* 根据 hideHeader prop 控制 header 显示 */}
      {!hideHeader && (
        <div className={styles["sticky-header-container"]}>
          <header className={styles["order-header"]}>
            <div className={styles["header-left"]}>
              <div className={styles["header-title"]}>
                <span className={styles["header-breadcrumb"]} onClick={() => navigate("/orders")} style={{ cursor: "pointer", color: "#666" }}>
                  訂單
                </span>
                <RightOutlined className={styles["header-separator"]} style={{ fontSize: "12px", margin: "0 8px" }} />
                <span>訂單紀錄/風險評估問卷</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {orderData && (
                <div className={styles["header-info"]}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                    <span className={`${styles["status-tag"]} ${styles[`status-${orderData.orderStatus || OrderStatus.Draft}`]}`}>
                      {ORDER_STATUS_DICT[orderData.orderStatus as OrderStatus] || "草稿"}
                    </span>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span className={styles["info-item"]} style={{ fontSize: 12 }}>
                        <span className={styles["info-label"]}>編號:</span> {orderData.orderNo || "-"}
                      </span>
                      {orderData.orderNo && (
                        <img
                          src={copyIcon}
                          alt="copy"
                          style={{ marginLeft: 4, cursor: "pointer", width: 12, height: 12 }}
                          onClick={() => {
                            navigator.clipboard.writeText(orderData.orderNo || "");
                            message.success("已複製");
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ width: 1, height: 32, background: "#E8E8E8", margin: "0 24px" }} />
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <span className={styles["info-item"]} style={{ fontSize: 12, lineHeight: "14px", color: "#999" }}>
                      投保人 / 受保人 :
                    </span>
                    <span className={styles["info-item"]} style={{ fontSize: 14, lineHeight: "20px", fontWeight: 500, color: "#333" }}>
                      {(orderData as any).policyholder?.basic?.chineseSurname || (orderData as any).policyholder?.name || ""}
                      {(orderData as any).policyholder?.basic?.chineseGivenName || ""}
                      <span style={{ color: "#E8E8E8", margin: "0 8px" }}>/</span>
                      {(orderData as any).insured?.basic?.chineseSurname || (orderData as any).insured?.name || ""}
                      {(orderData as any).insured?.basic?.chineseGivenName || ""}
                    </span>
                  </div>
                  <div style={{ width: 1, height: 32, background: "#E8E8E8", marginLeft: 24 }} />
                </div>
              )}
              <div className={styles["header-close"]} onClick={() => navigate("/orders")} style={{ cursor: "pointer", marginLeft: 16 }}>
                <CloseOutlined style={{ fontSize: 18, color: "#999" }} />
              </div>
            </div>
          </header>
        </div>
      )}

      <div style={{ padding: "24px", margin: "0 auto", width: "100%", overflowY: "auto" }}>
        <div className="flex justify-center items-center mb-6">
          <Typography.Title level={3} style={{ margin: 0 }}>
            客戶風險評估表
          </Typography.Title>
        </div>

        {/* CRA 審核結果 */}
        {originalCraData?.auditStatus && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 16px",
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 14,
                background: originalCraData.auditStatus === "APPROVED" ? "#f6ffed" : "#fff2f0",
                border: `1px solid ${originalCraData.auditStatus === "APPROVED" ? "#b7eb8f" : "#ffccc7"}`,
                color: originalCraData.auditStatus === "APPROVED" ? "#52c41a" : "#ff4d4f",
              }}
            >
              CRA審核結果：{originalCraData.auditStatus === "APPROVED" ? "審核通過" : "審核不通過"}
            </div>
            {originalCraData.auditStatus === "REJECTED" && originalCraData.auditReason && (
              <div
                style={{
                  marginTop: 8,
                  padding: "10px 16px",
                  background: "#fff2f0",
                  border: "1px solid #ffccc7",
                  borderRadius: 6,
                  color: "#ff4d4f",
                  fontSize: 14,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                不通過原因：{originalCraData.auditReason}
              </div>
            )}
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed} scrollToFirstError>
          {/* 1. Client Background */}
          <CustomCard title="客戶風險（客戶背景）" className="mb-4" bordered={false}>
            <Form.Item name="pep" label="客戶是否是政治公眾人物（PEP）嗎?" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="fullUboAndDirectorList" label="（僅限公司客戶）是否擁有所有實益擁有人和董事/控制人的完整名單" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly || true}>
                <Radio value="A">A: 是(公司客戶)</Radio>
                <Radio value="B">B: 否(公司客戶)</Radio>
                <Radio value="C">C: N/A(個人客戶)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item label="您的客戶有如下情況嗎？" required>
              <div style={{ background: "#F5F5F5", padding: "16px", borderRadius: "4px", marginBottom: "16px" }}>
                <Row gutter={[16, 8]}>
                  <Col span={12}>高淨值人士（淨值或投資資產&gt;1.5 億港元）</Col>
                  <Col span={12}>從事石油和天然氣行業</Col>
                  <Col span={12}>非營利組織</Col>
                  <Col span={12}>支付處理商</Col>
                </Row>
              </div>
              <Form.Item name="customerMediumRiskSituation" noStyle rules={[{ required: true, message: "請選擇" }]}>
                <Radio.Group disabled={isReadOnly}>
                  <Radio value="A" style={{ marginRight: 40 }}>
                    A. 是
                  </Radio>
                  <Radio value="B">B. 否</Radio>
                </Radio.Group>
              </Form.Item>
            </Form.Item>

            <Form.Item label="您的客戶有如下情況嗎？" required>
              <div style={{ background: "#F5F5F5", padding: "16px", borderRadius: "4px", marginBottom: "16px" }}>
                <Row gutter={[16, 8]}>
                  <Col span={12}>從事賭場和賭博行業</Col>
                  <Col span={12}>貴金屬 / 寶石 / 寶石經銷商</Col>
                  <Col span={12}>軍火商 / 製造商</Col>
                  <Col span={12}>貨幣服務業務</Col>
                  <Col span={12}>超高淨值客戶（淨值或投資資產 &gt; 2.5 億港元）</Col>
                  <Col span={12}>客戶有能力發行不記名股票的客戶（如果已知）</Col>
                </Row>
              </div>
              <Form.Item name="customerHighRiskSituation" noStyle rules={[{ required: true, message: "請選擇" }]}>
                <Radio.Group disabled={isReadOnly}>
                  <Radio value="A" style={{ marginRight: 40 }}>
                    A. 是
                  </Radio>
                  <Radio value="B">B. 否</Radio>
                </Radio.Group>
              </Form.Item>
            </Form.Item>
          </CustomCard>
          {/* 2. Identity & Address */}
          <CustomCard title="客戶風險（身份和地址驗證）" className="mb-4" bordered={false}>
            <Form.Item name="validIdentityDocuments" label="您的客戶是否提供了可接受的標準身份驗證檔案，例如身份證（對於個人客戶）或公司註冊證書（對於公司客戶）？" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="cooperative" label="您的客戶在過程中是否配合？ （不拖延提供身分和地址證明文件，回答所有問題等）" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>
          </CustomCard>
          {/* 3. Financial Status */}
          <CustomCard title="客戶風險（客戶的財務狀況）" className="mb-4" bordered={false}>
            <Form.Item name="sourceOfFundsConsistent" label="客戶陳述的財富來源 / 資金來源以及涉及的金額是否與您對客戶的了解相符，例如考慮到他們 的年齡和職業？" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="notHighRiskBusiness" label="您的客戶不涉及參與 / 經營高風險或高現金週轉業務？" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>
          </CustomCard>
          {/* 4. Premium Payment */}
          <CustomCard title="保費支付方式待確認如下" className="mb-4" bordered={false}>
            <Form.Item name="paidInitialPremium" label="投保申請時是否繳付了首期保費" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="fromOwnAccount" label="保費/投資金額是否來自客戶自己的賬戶" rules={[{ required: true }]}>
              <Radio.Group
                disabled={isReadOnly}
                onChange={(e) => {
                  if (e.target.value === "A") {
                    form.setFieldsValue({
                      thirdPartyPayerRelation: undefined,
                      thirdPartyPayerInfo: undefined,
                    });
                  }
                }}
              >
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, current) => prev.fromOwnAccount !== current.fromOwnAccount}>
              {({ getFieldValue }) => {
                const isThirdPartyPayer = getFieldValue("fromOwnAccount") === "B";
                return isThirdPartyPayer ? (
                  <>
                    <Form.Item name="thirdPartyPayerRelation" label="與第三方付款人關係" rules={[{ required: true, message: "請選擇關係" }]}>
                      <SelectInput
                        title="與第三方付款人關係"
                        placeholder="請選擇關係"
                        readOnly={isReadOnly}
                        options={[
                          { label: "配偶", value: "SPOUSE" },
                          { label: "父母", value: "PARENT" },
                          { label: "子女", value: "CHILD" },
                          { label: "受益人", value: "BENEFICIARY" },
                          { label: "其他", value: "OTHER" },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item label="上載關係證明檔案" required>
                      <div className="custom-upload-wrapper">{renderUploadBox(FileType.CraThiredPartyRelaTionShipCertificate, "第三方付款人關係證明")}</div>
                    </Form.Item>

                    <Form.Item name="thirdPartyPayerInfo" label="備註（選填）">
                      <Input.TextArea size="large" placeholder="請輸入其他補充資料" rows={3} disabled={isReadOnly} />
                    </Form.Item>
                  </>
                ) : null;
              }}
            </Form.Item>

            <Form.Item name="initialPremiumPaymentMethod" label="客戶計劃使用哪種或哪些方法來支付初始保費？（選一項或多項）" rules={[{ required: true }]}>
              <Checkbox.Group style={{ width: "100%" }} disabled={isReadOnly}>
                <Row>
                  <Col span={12}>
                    <Checkbox value="A">A: 信用卡</Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value="B">B: 銀行本票</Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value="C">C: 電匯</Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value="D">D: 銀行轉賬</Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value="E">E: 現金(透過保險公司或銀行櫃檯支付)</Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value="F">F: 自動扣款</Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value="G">G: 支票</Checkbox>
                  </Col>
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </CustomCard>
          {/* 5. Distribution Risk (Channel) */}
          <CustomCard title="分銷/交付風險（分銷管道）" className="mb-4" bordered={false}>
            <Form.Item name="referredByThirdParty" label="客戶是否由第三方介紹" rules={[{ required: true }]}>
              <Radio.Group
                disabled={isReadOnly}
                onChange={(e) => {
                  if (e.target.value === "B" || e.target.value === "C") {
                    // 选择 B 或 C 时，自动设置 instructionThroughThirdParty 为 C
                    form.setFieldsValue({
                      instructionThroughThirdParty: "C",
                      thirdPartyInstructionProof: undefined,
                    });
                  } else if (e.target.value === "A") {
                    // 选择 A 时，自动设置 instructionThroughThirdParty 为 B（且不可修改）
                    form.setFieldsValue({
                      instructionThroughThirdParty: "B",
                      thirdPartyInstructionProof: undefined,
                    });
                  }
                }}
              >
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
                <Radio value="C">C: N/A(介紹人是公司轉介人)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, current) => prev.referredByThirdParty !== current.referredByThirdParty}>
              {({ getFieldValue }) => {
                const referredByThirdParty = getFieldValue("referredByThirdParty");
                // 当选择 A / B / C 时，问题2应该禁用并按规则固定答案
                const isDisabled = referredByThirdParty === "A" || referredByThirdParty === "B" || referredByThirdParty === "C";

                return (
                  <Form.Item label="客戶的投保指示是否通過第三方傳達？" required={referredByThirdParty === "A"}>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <Form.Item
                        name="instructionThroughThirdParty"
                        noStyle
                        rules={[
                          {
                            required: referredByThirdParty === "A",
                            message: "請選擇",
                          },
                        ]}
                      >
                        <Radio.Group disabled={isReadOnly || isDisabled}>
                          <Radio value="A">A. 是</Radio>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, current) => prev.instructionThroughThirdParty !== current.instructionThroughThirdParty}>
                        {({ getFieldValue }) => {
                          const isYes = getFieldValue("instructionThroughThirdParty") === "A";
                          return (
                            <Form.Item name="thirdPartyInstructionProof" noStyle rules={[{ required: isYes, message: "請輸入原因" }]}>
                              <Input size="large" placeholder="請輸入原因" disabled={isReadOnly || !isYes} style={{ width: 200, marginRight: 24 }} />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>

                      <Form.Item name="instructionThroughThirdParty" noStyle>
                        <Radio.Group disabled={isReadOnly || isDisabled}>
                          <Radio value="B" style={{ marginRight: 24 }}>
                            B. 否
                          </Radio>
                          <Radio value="C">C. N/A</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </CustomCard>
          {/* 6. Distribution Risk (Face to Face) */}
          <CustomCard title="分銷/交付風險（面對面溝通）" className="mb-4" bordered={false}>
            <Form.Item name="faceToFaceCommunication" label="在投保過程中，您是否經面對面方式與客戶進行溝通? " rules={[{ required: true }]}>
              <Radio.Group
                disabled={isReadOnly}
                onChange={(e) => {
                  // 当选择"是"时，将问题二的值设置为 C（N/A）
                  if (e.target.value === "A") {
                    form.setFieldsValue({
                      needsNonFaceToFaceSafeguards: "C",
                    });
                  } else if (e.target.value === "B") {
                    // 当选择"否"时，清除问题二的值，让用户重新选择
                    form.setFieldsValue({
                      needsNonFaceToFaceSafeguards: undefined,
                    });
                  }
                }}
              >
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, current) => prev.faceToFaceCommunication !== current.faceToFaceCommunication}>
              {({ getFieldValue }) => {
                const faceToFaceCommunication = getFieldValue("faceToFaceCommunication");
                // 当选择 A 时，needsNonFaceToFaceSafeguards 固定为 C 且不可变更
                const isDisabled = faceToFaceCommunication === "A";

                return (
                  <Form.Item
                    name="needsNonFaceToFaceSafeguards"
                    label="在與客戶(非面對面）溝通的情況下，你是否認為需要有適當的保障措施來確認客戶行為或協議的真實性和 合法性 (包括：公證人見證簽名, 律師事務所保證協議合法, 會計師驗證財務文件等)"
                    rules={[{ required: faceToFaceCommunication === "B", message: "請選擇" }]}
                  >
                    <Radio.Group disabled={isReadOnly || isDisabled}>
                      <Radio value="A">A: 是</Radio>
                      <Radio value="B" disabled>
                        B: 否
                      </Radio>
                      <Radio value="C" disabled>
                        C: N/A
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </CustomCard>
          {/* 7. Country Risk */}
          <CustomCard title="國家或地區風險" className="mb-4" bordered={false}>
            <Form.Item name="residentInHK" label="您的客戶定居在香港嗎？" rules={[{ required: true }]}>
              <Radio.Group
                disabled={isReadOnly}
                onChange={(e) => {
                  // 当选择"是"或清空时，清除相关字段的值
                  if (e.target.value === "A" || !e.target.value) {
                    form.setFieldsValue({
                      nonHKResidenceCategory: undefined,
                    });
                  }
                }}
              >
                <Radio value="A">A: 是(LOW RISK)</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, current) => prev.residentInHK !== current.residentInHK}>
              {({ getFieldValue }) => {
                const residentInHK = getFieldValue("residentInHK");
                // 仅当选择"B: 否"时显示此问题
                if (!residentInHK || residentInHK === "A") {
                  return null;
                }

                return (
                  <Form.Item
                    name="nonHKResidenceCategory"
                    label="如果您的客戶不在香港定居，他/她是否位於下列國家之一？"
                    rules={[
                      {
                        required: residentInHK === "B",
                        message: "請選擇",
                      },
                    ]}
                  >
                    <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 12 }} disabled={isReadOnly}>
                      <Radio value="A" style={{ alignItems: "flex-start" }}>
                        <span style={{ whiteSpace: "normal" }}>
                          A. 澳大利亞，奧地利，比利時，加拿大，捷克共和國，丹麥，芬蘭，法國，德國，盧森堡，荷蘭，葡萄牙，斯洛文尼亞，西班牙，瑞典，美國，英國
                        </span>
                      </Radio>
                      <Radio value="B" style={{ alignItems: "flex-start" }}>
                        <span style={{ whiteSpace: "normal" }}>B. 文萊，中國，愛沙尼亞，希臘，日本，韓國，立陶宛，波蘭，卡塔爾，新加坡，中國臺灣，泰國</span>
                      </Radio>
                      <Radio value="C" style={{ alignItems: "flex-start" }}>
                        <span style={{ whiteSpace: "normal" }}>
                          C.
                          阿根廷，巴哈馬，巴林，孟加拉，巴巴多斯，百慕大，玻利維亞，巴西，保加利亞，柬埔寨，喀麥隆，賽普勒斯，多明尼加，杜拜，厄瓜多爾，埃及，埃塞俄比亞，斐濟，直布羅陀，關島，根西島，科威特，黎巴嫩，利比里亞，中國澳門，馬達加斯加，馬來西亞，馬爾代夫，馬里，馬爾他，模里西斯，墨西哥，密克羅尼西亞，摩納哥，蒙古，摩洛哥，緬甸，諾魯，尼泊爾，波多黎各，羅馬尼亞，俄羅斯聯邦，沙烏地阿拉伯，塞席爾，索羅門群島，南非，斯里蘭卡，蘇丹，瑞士，敘利亞，委內瑞拉，越南，英屬維京群島
                          (BVI)，維京群島（美國），也門
                        </span>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </CustomCard>
          {/* 8. Product Risk */}
          <CustomCard title="產品/服務風險" className="mb-4" bordered={false}>
            <Form.Item name="providesAnonymity" label="產品/向客戶提供的產品 / 服務本質上是否提供了更多的匿名性(例如: 信託保單/保險合約的持有人是一個多層架構的企業)？" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="involvesLargeCash" label="提供給客戶的產品/服務是否涉及收取大量現金？" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="frequentPurchasesWithoutReason" label="客戶在過去三個月內多次購買相同類型的保險產品，且沒有有效理由" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="supportsHighFrequencyIrregularActivity" label="這些產品/服務是否有利於高交易頻率和不規則的客戶賬戶活動？" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="allowsLargeWithdrawals" label="產品是否允許大額提款的產品 (例如: 保費融資, 投資相連人壽保險 (一次性繳付保費))" rules={[{ required: true }]}>
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="exceedsHighPremiumThreshold"
              label={
                <>
                  在職人士:年繳交保費≥HKD2,000,000 或一次性繳付保費（包含預繳）≥ HKD4,000,000。
                  <br />
                  非在職人士（家庭主婦/學生/退休人士/無業）或年齡在65歲以上的申請:年繳交保費≥HKD1,000,000 或一次性繳付保費（包含預繳）≥ HKD2,000,000。(累積客戶在過去六個月內購買保險產品的保費)
                </>
              }
              rules={[{ required: true, message: "請選擇是否符合高保費門檻" }]}
            >
              <Radio.Group disabled={isReadOnly}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B">B: 否</Radio>
              </Radio.Group>
            </Form.Item>

            {/* Optional: Card list for high premium details */}
            <Form.Item noStyle shouldUpdate={(prev, current) => prev.exceedsHighPremiumThreshold !== current.exceedsHighPremiumThreshold}>
              {({ getFieldValue }) =>
                getFieldValue("exceedsHighPremiumThreshold") === "A" ? (
                  <div className="mt-4">
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 8, color: "#333", fontWeight: 500 }}>若上方問題之答案為「是」者，請在下方詳細列舉客戶已投保的保單。</div>
                      <div style={{ fontWeight: "bold", fontSize: 16 }}>
                        年繳總交保費： {annualTotalPremium.toLocaleString()}HK$ &nbsp;&nbsp;
                        一次性繳付保費（含預繳）： {totalOneTimePayment.toLocaleString()}HK$
                      </div>
                    </div>

                    {/* Card List */}
                    <div style={{ marginBottom: 16 }}>
                      {productData.length === 0 ? (
                        <div
                          style={{
                            padding: 40,
                            textAlign: "center",
                            background: "#fafafa",
                            borderRadius: 8,
                            color: "#999",
                          }}
                        >
                          暫無外部保單，請點擊下方按鈕新增
                        </div>
                      ) : (
                        productData.map((order) => (
                          <HighPremiumOrderCard key={order.key} order={order} companyList={companyList} onEdit={() => edit(order)} onDelete={() => handleDelete(order.orderNo)} readOnly={isReadOnly} />
                        ))
                      )}
                    </div>

                    <div className="mt-4">
                      <CustomButton bgColor="#4DB6AC" color="white" onClick={add} style={{ padding: "0 16px" }} disabled={isReadOnly}>
                        新增外部保單
                      </CustomButton>
                    </div>
                  </div>
                ) : null
              }
            </Form.Item>
          </CustomCard>
          {/* Upload Section - Only show when exceedsHighPremiumThreshold is "A" (Yes) AND config is not empty */}
          <Form.Item noStyle shouldUpdate={(prev, current) => prev.exceedsHighPremiumThreshold !== current.exceedsHighPremiumThreshold}>
            {({ getFieldValue }) =>
              getFieldValue("exceedsHighPremiumThreshold") === "A" && craAttachmentConfig.length > 0 ? (
                <CustomCard title="請上載額外財務審核資料及檔案" className="mb-4" bordered={false}>
                  <style>{`
              .custom-upload-wrapper .ant-upload-select-picture-card,
              .custom-upload-wrapper .ant-upload-list-picture-card-container {
                width: 160px !important;
                height: 110px !important;
                margin-bottom: 8px !important;
              }
              .custom-upload-wrapper .ant-upload-list-item-container {
                width: auto !important;
                height: auto !important;
              }
              .upload-item-container {
                display: inline-flex;
                flex-direction: column;
                align-items: flex-start;
                margin-right: 32px;
                margin-bottom: 24px;
                vertical-align: top;
              }
              .upload-label {
                margin-top: 8px;
                color: #666;
                font-size: 14px;
                max-width: 160px;
                text-align: left;
                line-height: 1.4;
              }
              .required-mark {
                color: #ff4d4f;
                margin-right: 4px;
              }
              .sidebar-item {
                padding: 16px 24px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                cursor: pointer;
                border-left: 4px solid transparent;
                color: #666;
                font-weight: 500;
                transition: all 0.2s;
              }
              .sidebar-item:hover {
                background-color: #f9f9f9;
              }
              .sidebar-item.active {
                background-color: #E0F9F6;
                color: #00C7B6;
                border-left-color: #00C7B6;
              }
            `}</style>

                  <div className="custom-upload-wrapper" style={{ display: "flex", flexWrap: "wrap" }}>
                    {craAttachmentConfig.map((config) =>
                      config.type ? <div key={config.type}>{renderUploadBox(config.type as FileType, config.name || config.type, config.required || false)}</div> : null,
                    )}
                  </div>
                </CustomCard>
              ) : null
            }
          </Form.Item>
          <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg flex justify-between items-center z-50">
            <CustomButton height={35} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={handleBack} disabled={loading}>
              返回上一頁
            </CustomButton>
            <Space>
              <CustomButton height={35} bgColor="#4DB6AC" color="white" onClick={handleViewRiskResults}>
                查看四項評估結果
              </CustomButton>
              {!isReadOnly && (
                <CustomButton height={35} bgColor="#4DB6AC" color="white" loading={loading} onClick={handleSubmitClick}>
                  提交審核
                </CustomButton>
              )}
            </Space>
          </div>
          <div style={{ height: 80 }}></div> {/* Spacer for fixed footer */}
          {/* 签名弹窗 */}
          <Modal
            title="業務代表簽名"
            open={isSignatureModalOpen}
            onCancel={() => {
              setIsSignatureModalOpen(false);
              setIsSignatureReady(false);
            }}
            width={800}
            centered
            destroyOnClose
            footer={null}
          >
            <div
              style={{
                padding: 16,
                background: "#FFFBE6",
                borderBottom: "1px solid #FFE58F",
                color: "#FAAD14",
                textAlign: "center",
              }}
            >
              <ExclamationCircleFilled /> 請使用正楷簽名
            </div>
            <div
              style={{
                padding: 24,
                display: "flex",
                justifyContent: "center",
                background: "#f0f2f5",
              }}
            >
              <div style={{ background: "#fff", border: "1px solid #e8e8e8" }}>
                <SignaturePad
                  ref={signaturePadRef}
                  width={700}
                  height={300}
                  onSave={handleSaveSignature}
                  onContentChange={setIsSignatureReady}
                  showControls={false}
                  defaultValue={tempSignature}
                  backgroundName={currentUser?.name || ""}
                />
              </div>
            </div>
            <div
              style={{
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <CustomButton
                height={40}
                fontSize={16}
                bgColor="#fff"
                color="#666"
                style={{ border: "1px solid #d9d9d9" }}
                onClick={() => {
                  setIsSignatureModalOpen(false);
                  setIsSignatureReady(false);
                }}
              >
                取消
              </CustomButton>
              <Space>
                <CustomButton height={40} fontSize={16} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={() => signaturePadRef.current?.clear()}>
                  重寫
                </CustomButton>
                <CustomButton height={40} fontSize={16} bgColor="#4DB6AC" color="#fff" disabled={!isSignatureReady} onClick={handleConfirmSignature}>
                  確認並提交
                </CustomButton>
              </Space>
            </div>
          </Modal>
          <Modal title="風險評估結果" open={isRiskModalOpen} onCancel={() => setIsRiskModalOpen(false)} width={800} footer={null}>
            {loadingRiskResults ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Spin tip="載入風險評估結果中..." />
              </div>
            ) : (
              <div style={{ padding: 24 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e8e8e8" }}>
                      <th style={{ padding: 12, textAlign: "left" }}>評估項目</th>
                      <th style={{ padding: 12, textAlign: "left" }}>評估結果</th>
                      <th style={{ padding: 12, textAlign: "left" }}>原因</th>
                    </tr>
                  </thead>
                  <tbody>
                    {craResults.map((cra, index) => (
                      <tr key={cra.project || `cra-${index}`} style={{ borderBottom: "1px solid #e8e8e8" }}>
                        <td style={{ padding: 12 }}>{cra.project ? CRA_PROJECT_LABELS[cra.project] : "未知項目"}</td>
                        <td style={{ padding: 12 }}>{mapCraRiskResult(cra.result)}</td>
                        <td style={{ padding: 12 }}>{cra.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal>
          {/* High Premium Order Edit Modal */}
          <HighPremiumOrderModal
            visible={isOrderModalOpen}
            onCancel={() => {
              setIsOrderModalOpen(false);
              setEditingOrder(undefined);
            }}
            onSave={handleSaveOrder}
            initialValues={editingOrder}
            companyList={companyList}
            title={editingOrder ? "編輯外部保單" : "新增外部保單"}
          />
          {/* File Preview Modal */}
          <FilePreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} fileUrl={previewFile?.url} fileName={previewFile?.name} />
        </Form>
      </div>
    </div>
  );
};
